/* eslint-disable import/no-cycle */
/* eslint-disable max-len */
/* eslint-disable import/no-cycle */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import { FormattedMessage, useIntl } from 'react-intl';
import { generatePath } from 'react-router-dom';
import { Loader, SdirCheckbox, TextLink, Tooltip } from '@sdir/sds';
import { ErrorMessage } from '@sdir/utilities/lib/components';
import { useApi } from '@sdir/httpclient/lib/hooks/useApi';
import { AttachmentType } from '@sdir/httpclient/lib/clients/aps/application';
import { NewApplicationLayout } from '../..';
import { InputError } from '../../../Atoms';
import { QualificationBaseCard, QualificationAlternativeCard } from '../../../Molecules';
import {
  hasValidQualifications,
  hasValidAlternative,
  qualificationIsApproved,
} from '../../../../helpers/Qualifications';
import {
  useCurrentApplication,
  useAddedQualifications,
  useApplicationSteps,
  useAttachmentGroupWorkspace,
  useSubmitSequence,
  useFeatureToggles,
  useCurrentPerson,
  useCurrentUserInfo,
} from '../../../../services/hooks';
import { useQualificationsOpenSteps } from '../../../../services/hooks/useQulificationsOpenSteps';
import { AddedQualifications } from '../../../../types';
import {
  determineQualificationCardStatus,
  QualificationBaseHeader,
  QualificationCardStatus,
} from '../../../Molecules/QualificationBaseCard/QualificationBaseCard';
import { determineAlternativeStatus } from '../../../Molecules/QualificationAlternativeCard/QualificationAlternativeCard';
import EditApplicationGuard from '../../../Molecules/EditApplicationGuard/EditApplicationGuard';
import { overruleApi } from '../../../../httpclient';
import {
  buildCreateAttachmentGroupSequence,
  CreateAttachmentGroupTracker,
  SubmitAttachmentUpdatesTracker,
  SubmitAttachmentUpdatesParams,
  buildSubmitAttachmentUpdatesSequence,
} from '../../../../helpers/attachmentApiHelpers';
import { ToggleDisableNewSeagoing, isToggleOn } from '../../../../constants/permissions';
import Overrule from '../Overrule/Overrule';
import { useNavigateWithLang } from '../../../../services/hooks/useNavigateWithLang';

const Qualification: React.FC = () => {
  const intl = useIntl();
  const navigate = useNavigateWithLang();
  const [showValidation, setShowValidation] = useState(false);
  const [showOverruleValidation, setShowOverruleValidation] = useState(false);
  const [hasClickedNext, setHasClickedNext] = useState(false);
  const [showAllAlternatives, setShowAllAlternatives] = useState<boolean>(false);
  const {
    userInfo: { isExternal },
  } = useCurrentUserInfo();
  const {
    application,
    qualifications,
    loadQualifications,
    refresh: refreshApplication,
  } = useCurrentApplication({
    includeQualifications: true,
  });

  const { addedSeagoing, addedNewSeagoing, refreshNewSeagoing } = useAddedQualifications({
    all: true,
  });
  const { setSteps } = useQualificationsOpenSteps();
  const { nextStep } = useApplicationSteps();
  const [certificateChecked, setCertificateChecked] = useState(false);
  const [overruleComment, setOverruleComment] = useState('');
  const [overruleChecked, setOverruleChecked] = useState(false);
  const [certificateCheckedError, setCertificateCheckedError] = useState('');
  const featureToggle = useFeatureToggles();
  const {
    person: { result: currentPerson },
  } = useCurrentPerson();

  const enableNewSeagoing = !isToggleOn(
    featureToggle?.result,
    ToggleDisableNewSeagoing,
    currentPerson?.personalIdentificationNumber || ''
  );

  const {
    loading: overruleLoading,
    errorObject: overruleError,
    callApi: saveOverrule,
  } = useApi({
    apiFunction: (payload) => overruleApi.v1OverruleAttachCommandPut(payload),
    callApiOnInit: false,
  });

  const { attachmentGroupOverrule, attachmentsOverrule } = useMemo(() => {
    const attachments = application?.result?.attachments || [];
    const overruleAttachments = attachments.filter((a) => a.type === AttachmentType.OverRule);
    return {
      attachmentGroupOverrule: overruleAttachments?.[0]?.group || null,
      attachmentsOverrule: overruleAttachments,
    };
  }, [application?.result?.attachments]);

  const { availableFiles, changes, handleAddNewFiles, handleRemoveActiveFile } =
    useAttachmentGroupWorkspace(attachmentGroupOverrule || 'new');

  const certificateCheckNeeded =
    application?.result?.certificate === 'HHBF' ||
    application?.result?.certificate === 'HHBFV' ||
    application?.result?.certificate === 'HHBVS';

  const checkCertificate = useCallback(() => {
    if (certificateCheckNeeded && certificateChecked) {
      setCertificateCheckedError('');
      return false;
    }
    if (certificateCheckNeeded && !certificateChecked) {
      setCertificateCheckedError(
        intl.formatMessage({ id: 'qualification.certificatecheck.error' })
      );
      return true;
    }
    return false;
  }, [intl, certificateCheckNeeded, certificateChecked]);

  const addedQualificationsLoading = addedSeagoing.loading || addedNewSeagoing.loading;

  const addedQualifications: AddedQualifications = useMemo(
    () => ({
      seagoing: addedSeagoing.result || [],
      newSeagoing: addedNewSeagoing.result || {},
    }),
    [addedSeagoing.result, addedNewSeagoing.result]
  );

  const { allBaseItemsOk, allBaseItemsApprovedSinceBefore, anyBaseItemApprovedSinceBefore, ageOk } =
    useMemo(() => {
      if (qualifications?.result?.status === true) {
        return {
          ageOk: true,
          allBaseItemsOk: true,
          allBaseItemsApprovedSinceBefore: true,
          anyBaseItemApprovedSinceBefore: true,
        };
      }
      let countItemsOk = 0;
      let countItemsApprovedBefore = 0;
      let isAgeOk = false;
      let hasAgeRequirement = false;
      qualifications?.result?.base?.items?.forEach((q, i) => {
        const status: QualificationCardStatus = determineQualificationCardStatus(
          true,
          q,
          addedQualifications,
          application?.result?.attachments || [],
          application?.result?.confirmedQualifications || []
        );
        if (q?.uri?.indexOf('#PS_Age_') !== -1) {
          hasAgeRequirement = true;
          if (q.status === true) {
            isAgeOk = true;
          }
        }
        if (q.status === true) countItemsApprovedBefore += 1;
        if (status === QualificationCardStatus.Valid) countItemsOk += 1;
      });
      return {
        ageOk: isAgeOk || !hasAgeRequirement,
        allBaseItemsOk: !!(countItemsOk === qualifications?.result?.base?.items?.length),
        allBaseItemsApprovedSinceBefore: !!(
          countItemsApprovedBefore === qualifications?.result?.base?.items?.length
        ),
        anyBaseItemApprovedSinceBefore: countItemsApprovedBefore > 0,
      };
    }, [
      qualifications?.result?.base?.items,
      addedQualifications,
      application?.result?.attachments,
    ]);

  const { anyAlternativeOk, anyAlternativeOkSinceBefore } = useMemo(() => {
    if (qualifications?.result?.status === true) {
      return { anyAlternativeOk: true, anyAlternativeOkSinceBefore: true };
    }
    let isAnyOk = false;
    let isAnyOkSinceBefore = false;
    if (qualifications?.result?.alternatives?.length === 0) isAnyOk = true;
    qualifications?.result?.alternatives?.forEach((a) => {
      const status: QualificationCardStatus = determineAlternativeStatus(
        true,
        a,
        addedQualifications,
        application.result?.attachments || [],
        application.result?.confirmedQualifications || []
      );
      if (a.status === true) isAnyOkSinceBefore = true;
      if (status === QualificationCardStatus.Valid) isAnyOk = true;
    });
    return {
      anyAlternativeOk: isAnyOk,
      anyAlternativeOkSinceBefore: isAnyOkSinceBefore,
    };
  }, [qualifications?.result?.alternatives, addedQualifications, application.result]);

  const alternativesOk = useMemo(() => {
    return (
      qualifications.result &&
      application.result &&
      hasValidAlternative(
        qualifications.result,
        addedQualifications,
        application.result.attachments || [],
        application.result.confirmedQualifications || []
      )
    );
  }, [qualifications.result, application.result, addedQualifications]);

  const [submitAttachmentGroupTracker, setSubmitAttachmentGroupTracker] =
    useState<CreateAttachmentGroupTracker>({});
  const [submitAttachmentsTracker, setSubmitAttachmentsTracker] =
    useState<SubmitAttachmentUpdatesTracker>({});
  /* eslint-disable @typescript-eslint/indent */
  const { submit: submitAttachments } = useSubmitSequence<
    SubmitAttachmentUpdatesParams,
    SubmitAttachmentUpdatesTracker
  >(buildSubmitAttachmentUpdatesSequence);
  /* eslint-enable @typescript-eslint/indent */
  const { submit: submitAttachmentGroup } = useSubmitSequence(buildCreateAttachmentGroupSequence);

  const submitOverrule = async (checked: boolean) => {
    try {
      if (!application?.result) return;

      if (checked) {
        if (overruleComment?.length > 0) {
          setShowOverruleValidation(false);
          const res = await saveOverrule({
            applicationUid: application?.result?.uid,
            comment: overruleComment,
          });
          if (!res?.success) throw Promise.reject(res);
          const { newFiles, removedAttachments, updatedAttachments } = changes;
          if (!attachmentGroupOverrule) {
            // Create new attachment group
            await submitAttachmentGroup(
              {
                application: application.result,
                attachmentType: AttachmentType.OverRule,
                files: newFiles,
                simulatorResultItemUri: null,
              },
              submitAttachmentGroupTracker
            );
          } else {
            // Submit attachments, when we already have an attachment group
            const fileUpdateParams = {
              application: application.result,
              attachmentGroup: attachmentGroupOverrule,
              qualificationUri: null,
              updatedAttachments,
              removedAttachments,
              newAttachments: newFiles.map((x) => ({
                file: x,
                type: AttachmentType.OverRule,
              })),
            };
            await submitAttachments(fileUpdateParams, submitAttachmentsTracker);
          }
        } else {
          setShowOverruleValidation(true);
        }
      } else {
        // Cleanup potential overrule saved from before
        if (application?.result?.userComment) {
          const res = await saveOverrule({
            applicationUid: application?.result?.uid,
            comment: '',
          });
          if (!res?.success) throw Promise.reject(res);
        }
        if (attachmentGroupOverrule) {
          // Cleanup/remove all attachments of type OverRule
          const removedAttachments = attachmentsOverrule?.map((a) => a?.uid || '');
          const fileUpdateParams = {
            application: application.result,
            attachmentGroup: attachmentGroupOverrule,
            qualificationUri: null,
            updatedAttachments: [],
            removedAttachments: removedAttachments || [],
            newAttachments: [],
          };
          await submitAttachments(fileUpdateParams, submitAttachmentsTracker);
        }
      }
    } catch (error) {
      console.log('error: ', error);
      throw new Error();
    }
  };

  const handleNextClick = useCallback(async () => {
    // Special handling of overrule
    try {
      await submitOverrule(overruleChecked);
      if (overruleChecked && nextStep && application.result && !checkCertificate()) {
        if (overruleComment?.length > 0) {
          navigate(generatePath(nextStep.route, { applicationUid: application.result.uid || '' }));
        }
        return;
      }
      if (
        checkCertificate() ||
        !nextStep ||
        !qualifications.result ||
        !application.result ||
        !hasValidQualifications(
          qualifications.result,
          addedQualifications,
          application.result.attachments || [],
          application.result.confirmedQualifications || []
        )
      ) {
        setHasClickedNext(true);
        setShowValidation(true);
        setSteps(true, true);
        return;
      }
      navigate(generatePath(nextStep.route, { applicationUid: application.result.uid || '' }));
    } catch (err) {
      console.error(err);
    }
  }, [
    checkCertificate,
    nextStep,
    qualifications.result,
    application.result,
    addedQualifications,
    navigate,
    availableFiles,
    changes,
    overruleChecked,
    overruleComment,
    setSteps,
  ]);

  useEffect(() => {
    setOverruleChecked(!!application?.result?.userComment);
    setOverruleComment(application?.result?.userComment || '');
  }, [application?.result?.userComment]);

  useEffect(() => {
    refreshApplication?.();
    if (!isExternal) {
      refreshNewSeagoing?.();
    }
  }, [isExternal]);

  const isSid = ['SID', 'SID_duplicate'].includes(application?.result?.certificate || '');
  const showPreviouslyApproved =
    qualifications.result && application.result && anyBaseItemApprovedSinceBefore;
  const showBaseItems =
    qualifications.result && application.result && !allBaseItemsApprovedSinceBefore;
  const showAlternativeSection =
    !!qualifications.result?.alternatives?.length && application.result;
  const showLoader = application.loading || qualifications.loading || addedQualificationsLoading;
  const allOk = allBaseItemsOk && anyAlternativeOk;
  const showOverrule =
    !showLoader && qualifications.result && application.result && ageOk && !allOk && !isSid;

  return (
    <EditApplicationGuard>
      <NewApplicationLayout
        exitLabel={intl.formatMessage({ id: 'general.form.back' })}
        mainContentMaxWidth="100%"
        canNavBack
        canNavNext={!overruleLoading}
        navBackLabel={intl.formatMessage({ id: 'general.form.cancel' })}
        navNextLabel={intl.formatMessage({ id: 'general.form.next' })}
        onNavNextClick={handleNextClick}
        onNavBackClick={() => navigate(-1)}
      >
        <Wrapper>
          {showLoader && <Loader />}
          {application.error && <ErrorMessage error={application.error} />}
          {addedSeagoing.error && <ErrorMessage error={addedSeagoing.error} />}
          {addedNewSeagoing.error && <ErrorMessage error={addedNewSeagoing.error} />}
          {qualifications.error && (
            <ErrorMessage error={qualifications.error}>
              <TextLink
                text={intl.formatMessage({ id: 'general.button.retry' })}
                onClick={loadQualifications}
              />
            </ErrorMessage>
          )}
          {showPreviouslyApproved && (
            <>
              <H2>{intl.formatMessage({ id: 'missingdocumentation.page.base.title3' })}</H2>
              <PreviouslyApproved>
                {qualifications.result.base?.items?.map((q, i) => {
                  const isApproved = qualificationIsApproved(
                    q,
                    addedQualifications,
                    application?.result?.attachments || [],
                    application?.result?.confirmedQualifications || []
                  );
                  if (isApproved) {
                    return (
                      <QualificationBaseHeader key={q?.uri} qualification={q} status="valid" />
                    );
                  }
                  return undefined;
                })}
              </PreviouslyApproved>
            </>
          )}
          {showBaseItems && (
            <>
              <H2>
                {allBaseItemsOk
                  ? intl.formatMessage({ id: 'missingdocumentation.page.base.title2' })
                  : intl.formatMessage({ id: 'missingdocumentation.page.base.title' })}
                :
              </H2>
              <QualificationType>
                {qualifications.result.base?.items?.map((q, i) => {
                  const isApproved = qualificationIsApproved(
                    q,
                    addedQualifications,
                    application?.result?.attachments || [],
                    application?.result?.confirmedQualifications || []
                  );
                  if (isApproved) return undefined;
                  const status: QualificationCardStatus = determineQualificationCardStatus(
                    true,
                    q,
                    addedQualifications,
                    application?.result?.attachments || [],
                    application?.result?.confirmedQualifications || []
                  );
                  return (
                    <QualificationBaseCard
                      key={i}
                      qualification={q}
                      isNewApplication
                      addedQualifications={addedQualifications}
                      showValidation={showValidation}
                      cardStatus={status}
                    />
                  );
                })}
              </QualificationType>
            </>
          )}
          {showAlternativeSection && (
            <>
              <H2>
                {anyAlternativeOk && !showAllAlternatives ? (
                  <>
                    {intl.formatMessage({
                      id: 'missingdocumentation.page.alternative.titlefulfilled',
                    })}
                    :
                  </>
                ) : (
                  <FormattedMessage
                    id={
                      !showBaseItems
                        ? 'missingdocumentation.page.alternative.title2'
                        : 'missingdocumentation.page.alternative.title'
                    }
                    values={{ b: (chunks) => <b>{chunks}</b> }}
                  />
                )}
                {anyAlternativeOk && (
                  <StyledTextLink
                    text={intl.formatMessage({
                      id: showAllAlternatives
                        ? 'missingdocumentation.page.togglealternatives2'
                        : 'missingdocumentation.page.togglealternatives',
                    })}
                    size="16px"
                    click={() => setShowAllAlternatives(!showAllAlternatives)}
                  />
                )}
              </H2>
              <AlternativesWrapper fullWidth={isSid}>
                {qualifications.result?.alternatives?.map((a, i) => {
                  const status: QualificationCardStatus = determineAlternativeStatus(
                    true,
                    a,
                    addedQualifications,
                    application.result?.attachments || [],
                    application.result?.confirmedQualifications || []
                  );
                  const hideAlternative =
                    anyAlternativeOk &&
                    !showAllAlternatives &&
                    status !== QualificationCardStatus.Valid;
                  if (hideAlternative) return <React.Fragment key={i} />;
                  return (
                    <QualificationAlternativeCard
                      number={i + 1}
                      key={i}
                      isNewApplication
                      application={application.result}
                      qualifications={a}
                      addedQualifications={addedQualifications}
                      showValidation={showValidation && !alternativesOk}
                      cardStatus={status}
                      enableNewSeagoing={enableNewSeagoing}
                    />
                  );
                })}
              </AlternativesWrapper>
            </>
          )}
          {showOverrule && (
            <Overrule
              availableFiles={availableFiles}
              checked={overruleChecked}
              comment={overruleComment}
              showValidation={showOverruleValidation}
              error={overruleError}
              onNewFilesAdded={handleAddNewFiles}
              onDeleteFile={handleRemoveActiveFile}
              setOverruleChecked={setOverruleChecked}
              setOverruleComment={setOverruleComment}
              setShowOverruleValidation={setShowOverruleValidation}
            />
          )}
          {!showLoader && application?.result?.certificate && certificateCheckNeeded && (
            <>
              <CertificateCheck className={certificateChecked ? 'valid' : 'warning'}>
                <SdirCheckbox
                  checked={certificateChecked}
                  title={intl.formatMessage({ id: 'qualification.certificatecheck.title' })}
                  onClick={() => setCertificateChecked(!certificateChecked)}
                />
                <Tooltip width="40rem" placement="bottom-left">
                  {intl.formatMessage({ id: 'qualification.certificatecheck.tooltip' })}
                </Tooltip>
              </CertificateCheck>
              {certificateCheckedError !== '' && <InputError>{certificateCheckedError}</InputError>}
            </>
          )}
          {!showLoader && hasClickedNext && (!allBaseItemsOk || !anyAlternativeOk) && (
            <ErrorWrapper>
              <ErrorMessage
                title={intl.formatMessage({ id: 'app.requirements.missing.error.title' })}
                error={intl.formatMessage({ id: 'app.requirements.missing.error.desc' })}
              />
            </ErrorWrapper>
          )}
        </Wrapper>
      </NewApplicationLayout>
    </EditApplicationGuard>
  );
};

export default Qualification;

const Wrapper = styled.div`
  width: 100%;
`;

const H2 = styled.h2`
  font-size: 24px;
`;

export const StyledTextLink = styled(TextLink)`
  display: inline-block;
  margin-left: 30px;
`;

const ErrorWrapper = styled.div`
  max-width: 600px;
  margin-left: auto;
  margin-top: 20px;
`;

const QualificationType = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  grid-row-gap: 4rem;
  grid-column-gap: 3rem;
  justify-items: stretch;
  margin: 2rem 0 6rem 0;

  @media screen and (max-width: ${({ theme }) => theme.screen.xLarge}) {
    grid-template-columns: 1fr 1fr 1fr;
  }

  @media screen and (max-width: ${({ theme }) => theme.screen.large}) {
    grid-template-columns: 1fr 1fr;
  }

  @media screen and (max-width: ${({ theme }) => theme.screen.medium}) {
    grid-template-columns: 1fr;
  }
`;

const AlternativesWrapper = styled.div<{ fullWidth?: boolean }>`
  margin: 16px 0 32px 0;
  display: grid;
  grid-row-gap: 4rem;
  grid-column-gap: 3rem;
  grid-template-columns: 1fr 1fr 1fr 1fr;

  @media screen and (max-width: ${({ theme }) => theme.screen.xLarge}) {
    grid-template-columns: 1fr 1fr 1fr;
  }

  @media screen and (max-width: ${({ theme }) => theme.screen.large}) {
    grid-template-columns: 1fr 1fr;
  }

  @media screen and (max-width: ${({ theme }) => theme.screen.medium}) {
    grid-template-columns: 1fr;
  }

  ${({ fullWidth }) =>
    fullWidth
      ? css`
          grid-template-columns: 1fr !important;
        `
      : css`
          grid-template-columns: 1fr 1fr 1fr 1fr;
        `}
`;

const PreviouslyApproved = styled.div`
  margin-bottom: 40px;
  display: flex;
  flex-direction: column;

  & > div {
    margin-bottom: 10px;
  }
`;

const CertificateCheck = styled.div`
  margin-top: 32px;
  padding: 4rem;
  width: 81rem;
  display: flex;
  box-shadow: ${({ theme }) => theme.card.boxShadow};
  border-radius: ${({ theme }) => theme.card.borderRadius};
  background-color: ${({ theme }) => theme.colors.background.secondary};

  h2 {
    margin: 0 0 2rem 0;
  }

  &.warning {
    border: 1px solid ${({ theme }) => theme.colors.alert.warningDark};
    background-color: ${({ theme }) => theme.colors.alert.warningLight};
  }
  &.valid {
    border: 1px solid ${({ theme }) => theme.colors.alert.successDark};
    background-color: ${({ theme }) => theme.colors.alert.successLight};
  }

  @media screen and (max-width: ${({ theme }) => theme.screen.medium}) {
    width: 100%;
  }
`;
